import { TMetricQueryParams } from 'types/GlobalMetric'

import type { TAttributeOptionDto } from '@homepal/api-contract'
import { ParsedSegmentPath, SegmentPeriod } from 'types/GlobalWidget'
import { TCommentThread } from 'types/GlobalComment'
import { ETaskStatus, TSubTask } from 'types/GlobalTask'
import { TTask } from 'redux/hooks/Tasks'

import { TChartBand, TChartMarker } from 'components_new/molecules/charts/types'

export const COMMENT_CHAR = '“'
const SUB_TASK_MARKER_STYLE = {
  backgroundColor: '#000000',
  borderColor: '#ffffff',
  char: 'A',
  charColor: '#ffffff',
  radius: 8
}

const COMMENT_MARKER_STYLE = {
  backgroundColor: '#ffffff',
  borderColor: '#000000',
  char: COMMENT_CHAR,
  charColor: '#000000',
  radius: 8
}

/**
 * Sub-tasks on the day they were done.
 */
export const getSubTaskMarkers = (
  subTasks: TSubTask[],
  unnamedLabel: string
): TChartMarker[] =>
  subTasks.flatMap((subTask) =>
    subTask.status === ETaskStatus.DONE && subTask.completed_date
      ? [
          {
            ...SUB_TASK_MARKER_STYLE,
            date: subTask.completed_date,
            id: subTask.id,
            label: subTask.title || unnamedLabel
          }
        ]
      : []
  )

/**
 * Each started task's period, from started to done, or to now while it is
 * still in progress.
 */
export const getTaskBands = (tasks: TTask[]): TChartBand[] =>
  tasks.flatMap((task) =>
    task.started_date
      ? [
          {
            from: task.started_date,
            to: task.completed_date ?? new Date().toISOString()
          }
        ]
      : []
  )

/**
 * Comment threads at the point whose label they were made on.
 */
export const getCommentMarkers = (
  commentThreads: TCommentThread[],
  labels: string[]
): TChartMarker[] =>
  commentThreads.map((thread) => ({
    ...COMMENT_MARKER_STYLE,
    id: thread.id,
    index: labels.findIndex((label) => label === thread.data.label)
  }))

/**
 * Build a ParsedSegmentPath from metric raw data segment.
 * Mirrors the structure that dashboard widgets get from the backend,
 * so we can reuse the same date vs non-date processing logic.
 */
export const buildSegmentPath = (
  period: SegmentPeriod | null,
  segment: string,
  segmentOption: TAttributeOptionDto
): ParsedSegmentPath => ({
  attribute: segmentOption.attribute_id,
  filter_attribute: segmentOption.attribute_id,
  filter_value: segment,
  attribute_option_id: segmentOption.id,
  display_label: segment,
  label: segment,
  period,
  technical_name: segmentOption.technical_name,
  type: segmentOption.type
})

/**
 * Process a segment drill-down by updating query params.
 * Mirrors getSegmentSetters from dashboard widgets:
 * - Date segments → set from_date/to_date
 * - Non-date segments → add to filter using attribute
 */
export const processSegmentDrill = (
  attributeOptionId: string,
  segmentPath: ParsedSegmentPath,
  queryParams: TMetricQueryParams
): TMetricQueryParams => {
  if (segmentPath.period) {
    return {
      ...queryParams,
      from_date: segmentPath.period.from_date,
      segment_by: attributeOptionId,
      to_date: segmentPath.period.to_date
    }
  }

  const newFilter = queryParams.filter ? { ...queryParams.filter } : {}

  newFilter[segmentPath.attribute] = {
    eq: [String(segmentPath.label)]
  }

  return {
    ...queryParams,
    filter: newFilter,
    segment_by: attributeOptionId
  }
}
